import { Component, Input } from '@angular/core';

@Component({
    selector: 'app-expiry-badge',
    imports: [],
    templateUrl: './expiry-badge.component.html',
    styleUrls: ['./expiry-badge.component.scss']
})
export class ExpiryBadgeComponent {
  @Input() expiryDate: string | Date | undefined;
  @Input() expiredLabel = 'Offline payment expired';
  @Input() dueLabel = 'Payment due';
  /** IANA timezone of the test location (e.g. 'Australia/Melbourne'). When provided, the
   * due date/time is displayed in that timezone instead of the browser's local timezone. */
  @Input() timeZone: string | undefined;
  /** When true, shows a loading placeholder while the offline expiry is being fetched/calculated,
   * so the badge does not briefly flash a raw/fallback date before the correct value resolves. */
  @Input() loading = false;
  @Input() loadingLabel = 'Loading payment due date';

  get formattedDueDate(): string {
    if (!this.expiryDate) {
      return '';
    }

    const date = typeof this.expiryDate === 'string' ? new Date(this.expiryDate) : this.expiryDate;
    if (isNaN(date.getTime())) {
      return '';
    }

    const parts = this.getDateParts(date);
    const period = parts.hours >= 12 ? 'pm' : 'am';
    const displayHour = parts.hours % 12 || 12;
    const timeStr = parts.minutes > 0
      ? `${displayHour}:${parts.minutes.toString().padStart(2, '0')}${period}`
      : `${displayHour}${period}`;

    return `${parts.day} ${parts.month} ${parts.year}, ${timeStr}`;
  }

  private getDateParts(date: Date): { day: number; month: string; year: number; hours: number; minutes: number } {
    if (this.timeZone) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this.timeZone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });
      const parts = formatter.formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {} as Record<string, string>);
      // 'hour' can be '24' at midnight in some locales; normalise to 0.
      const hours = parseInt(parts['hour'], 10) % 24;
      return {
        day: parseInt(parts['day'], 10),
        month: parts['month'],
        year: parseInt(parts['year'], 10),
        hours,
        minutes: parseInt(parts['minute'], 10)
      };
    }

    return {
      day: date.getDate(),
      month: date.toLocaleString('en-US', { month: 'short' }),
      year: date.getFullYear(),
      hours: date.getHours(),
      minutes: date.getMinutes()
    };
  }

}
